/**
 * Optimal-teams result filters — min/max bounds on the three team-level
 * metrics the enumerator computes. Applied INSIDE the enumeration (not on
 * the returned top-N) so "min +1.5M price change" surfaces every qualifying
 * team, not just those that happened to rank in the unfiltered top 1000.
 */

export interface TeamFilters {
  minPoints?: number;
  maxPoints?: number;
  minBudgetChange?: number;
  maxBudgetChange?: number;
  minCost?: number;
  maxCost?: number;
}

export function hasActiveFilters(
  filters: TeamFilters | null | undefined,
): boolean {
  if (!filters) return false;
  return Object.values(filters).some((v) => v !== undefined);
}

export function countActiveFilters(
  filters: TeamFilters | null | undefined,
): number {
  if (!filters) return 0;
  return Object.values(filters).filter((v) => v !== undefined).length;
}

/** Epsilon so a team predicted at exactly the bound passes despite float noise. */
const EPS = 1e-9;

export function teamPassesFilters(
  totalCost: number,
  predictedPoints: number,
  predictedBudgetChange: number,
  filters: TeamFilters | null | undefined,
): boolean {
  if (!filters) return true;
  if (
    filters.minPoints !== undefined &&
    predictedPoints < filters.minPoints - EPS
  )
    return false;
  if (
    filters.maxPoints !== undefined &&
    predictedPoints > filters.maxPoints + EPS
  )
    return false;
  if (
    filters.minBudgetChange !== undefined &&
    predictedBudgetChange < filters.minBudgetChange - EPS
  )
    return false;
  if (
    filters.maxBudgetChange !== undefined &&
    predictedBudgetChange > filters.maxBudgetChange + EPS
  )
    return false;
  if (filters.minCost !== undefined && totalCost < filters.minCost - EPS)
    return false;
  if (filters.maxCost !== undefined && totalCost > filters.maxCost + EPS)
    return false;
  return true;
}
